import React, {
	Fragment,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { di } from 'react-magnetic-di';
import {
	PRODUCT_DISCOVERY_PROJECT,
	SERVICE_DESK_PROJECT,
	toProjectType,
} from '@jira/platform__project-types/src/project-types.tsx';
import { isWorkItemModernizationGatesEnabled } from '@jira/issue__eligibility/src/isWorkItemModernizationGatesEnabled.tsx';
import { useRightSidebarController } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { useProject } from '@jira/navigation-apps__sidebar-common/src/controllers/project/index.tsx';
import { HELP_PANEL_ID } from '@jira/platform__help-panel-common-constants/src/index.tsx';
import { componentWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { createRouterSelector, useQueryParam } from '@atlassian/react-resource-router';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	useGlobalObjectPreviewPanels as usePlatformGlobalObjectPreviewPanels,
	usePreviewPanelIFrameCommunication,
} from '@atlassian/preview-panel-global-objects/global-object-preview-panels';
import { asGlobalObjectType } from '@atlassian/preview-panel-global-objects/utils';
import type { GlobalObjectType } from '@atlassian/preview-panel-global-objects/types';
import { PreviewPanelsProvider as PlatformPreviewPanelsProvider } from '@atlassian/preview-panels-api/preview-panel-context';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api/use-preview-panel-actions';
import type { Panel } from '@atlassian/preview-panels-api/types';
import { useCurrentPreviewPanel } from '@atlassian/preview-panels-api/use-current-preview-panel';
import { PreviewPanelsNavigationProvider } from '@atlassian/preview-panels-api/preview-panel-navigation';
import { CONVERSATION_ASSISTANT_CONTAINER_ID } from '@atlassian/conversation-assistant/constants';
import { useIsNotListViewRoute } from './useIsNotListViewRoute.tsx';
import { useListViewPreviewPanelMaxWidth } from './useListViewPreviewPanelMaxWidth.tsx';
import { isNativeIssueViewPreviewPanel } from './isNativeIssueViewPreviewPanel.tsx';
import {
	JiraPreviewPanelsContext,
	type PreviewPanelEvent,
	type IssuePreviewPanelsNavigationRef,
} from './JiraPreviewPanelsContext.tsx';
import { JiraPreviewPanelsNavigationProvider } from './JiraPreviewPanelsNavigationProvider.tsx';
import { useEmbeddedConfluencePanelCommunication } from './useEmbeddedConfluencePanelCommunication.tsx';
import { useInitialPanel } from './useInitialPanel.tsx';
import { useInitialPanelNavigationRef } from './useInitialPanelNavigationRef.tsx';
import { useInitialPanelWidth } from './useInitialPanelWidth.tsx';

const MaybePreviewPanelsNavigationProvider = componentWithCondition(
	() =>
		fg('jivtech-1920-back-navigation-gpp') ||
		expVal('nin_issue_preview_panel_navigation', 'isEnabled', false),
	PreviewPanelsNavigationProvider,
	Fragment,
);

const MaybeJiraPreviewPanelsNavigationProvider = componentWithCondition(
	() =>
		fg('jivtech-1920-back-navigation-gpp') ||
		expVal('nin_issue_preview_panel_navigation', 'isEnabled', false),
	JiraPreviewPanelsNavigationProvider,
	Fragment,
);

type NotifyCallback = (event: PreviewPanelEvent) => void;

const parentProduct: string = 'jira-software';

/**
 * Only return the route name and path if the route is preview panel enabled.
 * Return as a string to ensure the value is stable and avoid unnecessary re-renders.
 */
// oxlint-disable-next-line typescript/no-invalid-void-type
export const useEnabledRoute: (args_0: void) => string | null = createRouterSelector<string | null>(
	({ route, location }) => {
		if (!route.supportsPreviewPanel) {
			return null;
		}
		// Some routes (e.g. NIN) intermittently add/remove a trailing slash from the pathname,
		// which would cause the panel to close unexpectedly. Strip it for a stable key.
		const pathname = fg('nin_glance_panel_technical_changes')
			? location.pathname.replace(/\/$/, '')
			: location.pathname;
		return `${route.name}:${pathname}`;
	},
);

const isJiraIssueAri = (ari: string): boolean =>
	ari.startsWith('ari:cloud:jira:') && ari.includes(':issue/');

const JiraPreviewPanelsProviderOld = ({
	children,
	navigation,
	subscribe,
}: {
	children: ReactNode;
	navigation: IssuePreviewPanelsNavigationRef;
	subscribe: (callback: NotifyCallback) => () => void;
}) => {
	di(window);
	const cloudId = useCloudId();
	const { close } = usePreviewPanelsActions();
	const enabledRoute = useEnabledRoute();
	const isIssueEmbed = enabledRoute?.split(':')[0] === 'issue-embed';
	const previewPanelMaxWidth = useListViewPreviewPanelMaxWidth();
	let SUPPORT_PANEL_TYPES_MILESTONE_ONE: GlobalObjectType[] = [];

	const { data: project } = useProject();
	const projectType = toProjectType(project?.type);
	const isJSMOrJPDProject =
		projectType === SERVICE_DESK_PROJECT || projectType === PRODUCT_DISCOVERY_PROJECT;

	SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('townsquare-project');
	SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('jira-issue');
	SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('identity-team');

	/**
	 * Update Jira Preview Panel integration for different products
	 * (Confluence, Goals, Team, Project) behind different FGs.
	 * This is to have ability to control different product integrations using FG.
	 * So, that we should be able to turn specific product ON/OFF individually.
	 */
	SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('townsquare-goal');

	SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('confluence-page');

	if (fg('jira-preview-panel-assets-integration')) {
		SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('cmdb-object');
	}

	if (isJSMOrJPDProject) {
		SUPPORT_PANEL_TYPES_MILESTONE_ONE = [];
	}

	let supportedObjects: GlobalObjectType[] | undefined = SUPPORT_PANEL_TYPES_MILESTONE_ONE;

	// If the route is an issue embed, we can get the supported objects from the URL search params.
	// This allows us to support custom objects in the preview panel for the host application.
	if (isIssueEmbed && typeof window !== 'undefined') {
		const urlSearchParams = new URLSearchParams(window.location.search);
		supportedObjects = urlSearchParams.get('previewPanels')?.split(',')?.map(asGlobalObjectType);
	}

	const { isPreviewPanelAvailable, isPreviewRestricted, openPreviewPanel } =
		usePlatformGlobalObjectPreviewPanels('jira', cloudId, {
			supportedObjects,
			enabled: enabledRoute !== null,
			...(previewPanelMaxWidth && { maxWidth: previewPanelMaxWidth }),
		});

	usePreviewPanelIFrameCommunication(openPreviewPanel);
	useEmbeddedConfluencePanelCommunication({ closePreviewPanel: close });

	useEffect(
		() => () => {
			// automatically close the preview panel when the route changes
			close();
		},
		[enabledRoute, close],
	);

	const value = useMemo(
		() => ({
			subscribe,
			openPreviewPanel,
			isPreviewPanelAvailable,
			isPreviewRestricted,
			navigation,
			closePreviewPanel: close,
		}),
		[subscribe, openPreviewPanel, isPreviewPanelAvailable, isPreviewRestricted, close, navigation],
	);

	return (
		<JiraPreviewPanelsContext.Provider value={value}>{children}</JiraPreviewPanelsContext.Provider>
	);
};

const JiraPreviewPanelsProviderNew = ({
	children,
	navigation,
	subscribe,
}: {
	children: ReactNode;
	navigation: IssuePreviewPanelsNavigationRef;
	subscribe: (callback: NotifyCallback) => () => void;
}) => {
	di(window);
	const cloudId = useCloudId();
	const { close } = usePreviewPanelsActions();
	const enabledRoute = useEnabledRoute();
	const isIssueEmbed = enabledRoute?.split(':')[0] === 'issue-embed';
	const previewPanelMaxWidth = useListViewPreviewPanelMaxWidth();
	const currentPanel = useCurrentPreviewPanel();
	let SUPPORT_PANEL_TYPES_MILESTONE_ONE: GlobalObjectType[] = [];

	const { data: project } = useProject();
	const projectType = toProjectType(project?.type);
	const isJSMOrJPDProject =
		projectType === SERVICE_DESK_PROJECT || projectType === PRODUCT_DISCOVERY_PROJECT;

	SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('townsquare-project');
	SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('jira-issue');
	SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('identity-team');

	/**
	 * Update Jira Preview Panel integration for different products
	 * (Confluence, Goals, Team, Project) behind different FGs.
	 * This is to have ability to control different product integrations using FG.
	 * So, that we should be able to turn specific product ON/OFF individually.
	 */
	SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('townsquare-goal');

	SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('confluence-page');

	if (fg('jira-preview-panel-assets-integration')) {
		SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('cmdb-object');
	}

	if (isJSMOrJPDProject) {
		SUPPORT_PANEL_TYPES_MILESTONE_ONE = [];
	}

	let supportedObjects: GlobalObjectType[] | undefined = SUPPORT_PANEL_TYPES_MILESTONE_ONE;

	// If the route is an issue embed, we can get the supported objects from the URL search params.
	// This allows us to support custom objects in the preview panel for the host application.
	if (isIssueEmbed && typeof window !== 'undefined') {
		const urlSearchParams = new URLSearchParams(window.location.search);
		supportedObjects = urlSearchParams.get('previewPanels')?.split(',')?.map(asGlobalObjectType);
	}

	const {
		isPreviewPanelAvailable,
		isPreviewRestricted,
		openPreviewPanel: openPlatformPreviewPanel,
	} = usePlatformGlobalObjectPreviewPanels('jira', cloudId, {
		supportedObjects,
		enabled: enabledRoute !== null,
		...(previewPanelMaxWidth && { maxWidth: previewPanelMaxWidth }),
	});

	const isNativeIssuePanelOpen =
		currentPanel != null && isNativeIssueViewPreviewPanel(currentPanel);

	const openPreviewPanel = useCallback(
		(props: {
			url: string;
			ari: string;
			name: string;
			iconUrl?: string;
			parentProduct?: string;
		}) => {
			if (
				isNativeIssuePanelOpen &&
				isJiraIssueAri(props.ari) &&
				currentPanel?.onSamePanelTypeNavigate
			) {
				const didHandleSamePanelTypeNavigate = currentPanel.onSamePanelTypeNavigate({
					ari: props.ari,
					url: props.url,
				});

				if (didHandleSamePanelTypeNavigate) {
					return;
				}
			}

			openPlatformPreviewPanel(props);
		},
		[isNativeIssuePanelOpen, currentPanel, openPlatformPreviewPanel],
	);

	usePreviewPanelIFrameCommunication(openPreviewPanel);
	useEmbeddedConfluencePanelCommunication({ closePreviewPanel: close });

	useEffect(
		() => () => {
			// automatically close the preview panel when the route changes
			close();
		},
		[enabledRoute, close],
	);

	const value = useMemo(
		() => ({
			subscribe,
			openPreviewPanel,
			navigation,
			isPreviewPanelAvailable,
			isPreviewRestricted,
			closePreviewPanel: close,
		}),
		[subscribe, openPreviewPanel, isPreviewPanelAvailable, isPreviewRestricted, close, navigation],
	);

	return (
		<JiraPreviewPanelsContext.Provider value={value}>{children}</JiraPreviewPanelsContext.Provider>
	);
};

const JiraPreviewPanelsProvider = componentWithCondition(
	isWorkItemModernizationGatesEnabled,
	JiraPreviewPanelsProviderNew,
	JiraPreviewPanelsProviderOld,
);

export const PreviewPanelsProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
	const subscribers = useRef<Set<NotifyCallback>>(new Set());
	const { closeRightSidebar } = useRightSidebarController();
	const [, setSelectedIssue] = useQueryParam('selectedIssue');
	const isNotListView = useIsNotListViewRoute();

	const isNotListViewRef = useRef(isNotListView);
	isNotListViewRef.current = isNotListView;

	const notifySubscribers = useCallback((event: PreviewPanelEvent) => {
		subscribers.current.forEach((callback) => callback(event));
	}, []);

	const handlePreviewPanelOpen = useCallback(
		(panel: Panel) => {
			// Close Rovo Chat when a preview panel opens
			closeRightSidebar(CONVERSATION_ASSISTANT_CONTAINER_ID);
			// Close Help Panel when a preview panel opens
			closeRightSidebar(HELP_PANEL_ID);
			// Close legacy Embedded Confluence Side Panel when a preview panel opens
			globalThis.window?.postMessage({ type: 'CLOSE_EMBEDDED_CONFLUENCE_PANEL' }, '*');

			notifySubscribers({ action: 'open', panel });

			if (
				!isNativeIssueViewPreviewPanel(panel) &&
				isNotListViewRef.current &&
				!fg('jivtech-1920-back-navigation-gpp')
			) {
				setSelectedIssue?.(undefined);
			}
		},
		[closeRightSidebar, notifySubscribers, setSelectedIssue],
	);

	const handlePreviewPanelClose = useCallback(
		(panel: Panel) => {
			notifySubscribers({ action: 'close', panel });
		},
		[notifySubscribers],
	);

	const subscribe = useCallback((callback: NotifyCallback) => {
		subscribers.current.add(callback);

		return () => {
			subscribers.current.delete(callback);
		};
	}, []);

	const initialPanelNavigationRef = useInitialPanelNavigationRef();
	const initialPanel = useInitialPanel(initialPanelNavigationRef);
	const initialPanelWidth = useInitialPanelWidth();

	return (
		<PlatformPreviewPanelsProvider
			onPreviewPanelOpen={handlePreviewPanelOpen}
			onPreviewPanelClose={handlePreviewPanelClose}
			parentProduct={parentProduct}
			{...(fg('nin_glance_panel_technical_changes') && { initialPanel })}
			{...(expVal('nin_glance_panel', 'isEnabled', false) && { initialWidth: initialPanelWidth })}
		>
			<MaybePreviewPanelsNavigationProvider>
				<JiraPreviewPanelsProvider navigation={initialPanelNavigationRef} subscribe={subscribe}>
					<MaybeJiraPreviewPanelsNavigationProvider>
						{children}
					</MaybeJiraPreviewPanelsNavigationProvider>
				</JiraPreviewPanelsProvider>
			</MaybePreviewPanelsNavigationProvider>
		</PlatformPreviewPanelsProvider>
	);
};

export const usePreviewPanelStateChanged = (callback: NotifyCallback): void => {
	const { subscribe } = useContext(JiraPreviewPanelsContext) ?? {};

	useEffect(() => {
		if (!subscribe) return;

		const unsubscribe = subscribe(callback);
		return () => unsubscribe();
	}, [subscribe, callback]);
};
